/**
 * Global "4 generations at a time" limit UX.
 *
 * Every generation-start endpoint returns HTTP 429 with
 * `code: 'generation_limit_reached'` when the store already has 4
 * running jobs. Call sites detect it here and fire a window event; the
 * GenerationLimitToast (mounted once in AppLayout) shows a top-of-page
 * popup for 8 seconds — the same everywhere, including the AI chat.
 */
import { isUpgradeError, showUpgradeToast } from './plansModal';

export const GENERATION_LIMIT_EVENT = 'autonoma:generation-limit';

export const GENERATION_LIMIT_MESSAGE =
  'You can only run 4 generations at a time. Please wait for one to finish before starting another.';

export function isGenerationLimitError(res, data) {
  return res?.status === 429 || data?.code === 'generation_limit_reached';
}

export function showGenerationLimitToast() {
  try {
    window.dispatchEvent(new CustomEvent(GENERATION_LIMIT_EVENT));
  } catch { /* SSR/test safety */ }
}

/**
 * Fetch-handler helper: when the response is the limit error, shows the
 * toast and throws an error tagged `isGenerationLimit` — catch blocks
 * check the tag to skip their local error banner (the toast is the UX).
 *
 * Also handles PLAN limit errors (403 + upgrade: quota exhausted /
 * feature not in plan / changes budget used up) with the upgrade toast
 * + plans modal, so every existing generation call site gets Stage C
 * enforcement UX for free.
 */
export function throwIfGenerationLimit(res, data) {
  if (isUpgradeError(res, data)) {
    // code: quota cap → the PlanLimitOverlay already opened from the
    // central fetch wrapper (lib/api.js); gate → the "See plans" toast.
    // Either way the call site skips its own banner (isGenerationLimit).
    showUpgradeToast(data?.error || 'This needs a bigger plan.', data?.feature, data?.code || null);
    const err = new Error(data?.error || 'Not available on your plan.');
    err.isGenerationLimit = true; // reuse the tag: call sites skip local banners
    err.isUpgradeRequired = true;
    throw err;
  }
  if (!isGenerationLimitError(res, data)) return;
  showGenerationLimitToast();
  const err = new Error(data?.error || GENERATION_LIMIT_MESSAGE);
  err.isGenerationLimit = true;
  throw err;
}

// Re-exported so call sites can import everything from one place.
export { isUpgradeError, showUpgradeToast, openPlansModal } from './plansModal';
